// @flow
import api, { postLiveCatalogRegistration } from '../api/catalogRegistration';
import { getAuthToken, getBidderEmail, LOAD_BIDDER_DATA_SUCCESS, LOG_OUT_BIDDER } from './user';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getBidSource } from './liveBid';
import { getDeployment } from './config';
import { getLiveCatalogData } from './console';
import type { GlobalState } from '../rootReducer';
import { LOGIN_SUCCESS } from './login';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';
import { REGISTER_FOR_CATALOG_SUCCESS } from './registerForCatalog';
import union from 'lodash/union';

/* Action Types */
export const LOAD_LIVE_CATALOG_REGISTRATION_FAIL = 'la/domain/liveCatalogRegistration/LOAD_FAIL';
export const LOAD_LIVE_CATALOG_REGISTRATION_REQUEST = 'la/domain/liveCatalogRegistration/LOAD_REQUEST';
export const LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS = 'la/domain/liveCatalogRegistration/LOAD_SUCCESS';

export const LOAD_UPCOMING_CATALOG_REGISTRATION_FAIL = 'la/domain/upcomingCatalogRegistration/LOAD_FAIL';
export const LOAD_UPCOMING_CATALOG_REGISTRATION_REQUEST = 'la/domain/upcomingCatalogRegistration/LOAD_REQUEST';
export const LOAD_UPCOMING_CATALOG_REGISTRATION_SUCCESS = 'la/domain/upcomingCatalogRegistration/LOAD_SUCCESS';

const createCatalogRegistration = (catalogId, approvalStatus, bidLimit = 0) => ({
    approved: approvalStatus === 1,
    bidLimit: bidLimit,
    blockedByAuctioneer: approvalStatus === 4,
    catalogId: catalogId,
    declined: approvalStatus === 2,
    limited: approvalStatus === 3,
    pending: approvalStatus === 0,
    registered: true,
    suspended: approvalStatus === 5,
});

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_LIVE_CATALOG_REGISTRATION_FAIL:
        case LOAD_UPCOMING_CATALOG_REGISTRATION_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_LIVE_CATALOG_REGISTRATION_REQUEST:
        case LOAD_UPCOMING_CATALOG_REGISTRATION_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case REGISTER_FOR_CATALOG_SUCCESS:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.payload.catalogId]: createCatalogRegistration(
                        action.payload.catalogId,
                        action.payload.approvalStatus,
                        action.payload.bidLimit,
                    ),
                },
            };
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
        case LOAD_UPCOMING_CATALOG_REGISTRATION_SUCCESS:
            time = action.meta.actionTime;
            loaded = { ...state.loaded };
            loading = [...state.loading];
            loading = difference(loading, [action.meta.catalogId]);
            existing = cloneDeep(state.byId);
            if (action.payload.catalogRegistration) {
                action.payload.catalogRegistration.forEach(catalog => {
                    existing[catalog.catalogId] = catalog;
                    existing[catalog.catalogId].registered =
                        catalog.approved ||
                        catalog.blockedByAuctioneer ||
                        catalog.declined ||
                        catalog.limited ||
                        catalog.pending ||
                        catalog.suspended;
                    loaded[catalog.catalogId] = time;
                });
            }
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS:
            time = action.meta.actionTime;
            loaded = { ...state.loaded, [action.meta.catalogId]: time };
            loading = [...state.loading];
            loading = difference(loading, [action.meta.catalogId]);

            const existingCatalog = state.byId[action.meta.catalogId] || {};
            existing = cloneDeep(state.byId);
            existing[action.meta.catalogId] = existing[action.meta.catalogId] || {};
            existing[action.meta.catalogId].bidLimit = action.payload.bidLimit
                ? action.payload.bidLimit
                : existing[action.meta.catalogId].bidLimit;

            if (action.payload.approved && !existingCatalog.approved) {
                existing[action.meta.catalogId].approved = true;
                existing[action.meta.catalogId].pending = false;
                existing[action.meta.catalogId].declined = false;
                existing[action.meta.catalogId].registered = true;
                return {
                    ...state,
                    byId: existing,
                    loaded,
                    loading,
                };
            } else if (!action.payload.approved && existingCatalog.approved) {
                existing[action.meta.catalogId].approved = false;
                existing[action.meta.catalogId].pending = false;
                existing[action.meta.catalogId].declined = true;
                existing[action.meta.catalogId].registered = false;
                existing[action.meta.catalogId].bidLimit = 0;
                existing[action.meta.catalogId].limited = false;
                return {
                    ...state,
                    byId: existing,
                    loaded,
                    loading,
                };
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };

        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };

        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogRegistration;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getCatalogRegistration = createSelector([byIdSelector, idSelector], (byId, id) => {
    return (
        byId[id] || {
            approved: false,
            bidLimit: 0,
            blockedByAuctioneer: false,
            declined: false,
            limited: false,
            pending: false,
            registered: false,
            suspended: false,
        }
    );
});

export const getLoadTimeForCatalogRegistration = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0,
);

export const isCatalogRegistrationLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id),
);

export const getAllCurrentUserCatalogRegistrationIds = (state: any) => {
    return Object.keys(state.catalogRegistration.byId)
        .filter(key => state.catalogRegistration.byId[key].status !== 'done')
        .map<number>(catIdString => Number(catIdString));
};

const fetchLiveCatalogRegistration = catalogId => async (dispatch, getState) => {
    const state = getState();
    try {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const email = getBidderEmail(state);
        const { approved: wasApproved } = getLiveCatalogData(state, catalogId);
        const source = getBidSource(state);

        dispatch({
            meta: { actionTime: Date.now() },
            payload: catalogId,
            type: LOAD_LIVE_CATALOG_REGISTRATION_REQUEST,
        });
        const response = await postLiveCatalogRegistration({ authToken, catalogId, deployment, email, source });
        dispatch({
            meta: { actionTime: Date.now(), catalogId },
            payload: { ...response.data, catalogId, created: new Date().getTime(), wasApproved },
            type: LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { catalogId },
            payload: error,
            type: LOAD_LIVE_CATALOG_REGISTRATION_FAIL,
        });
    }
};

const shouldFetchLiveCatalogRegistration = (state, catalogId) => {
    const authToken = getAuthToken(state);
    if (!catalogId || !authToken) return false;
    const loading = isCatalogRegistrationLoading(state, catalogId);
    return !loading;
};

export const fetchLiveCatalogRegistrationIfNeeded = (catalogId: number) => async (
    dispatch: Function,
    getState: Function,
) => {
    const shouldFetch = shouldFetchLiveCatalogRegistration(getState(), catalogId);
    if (shouldFetch) {
        return dispatch(fetchLiveCatalogRegistration(catalogId));
    }
    return Promise.resolve();
};

const fetchUpcomingCatalogRegistration = catalogId => async (dispatch, getState) => {
    const state = getState();
    try {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        if (authToken) {
            dispatch({
                payload: catalogId,
                type: LOAD_UPCOMING_CATALOG_REGISTRATION_REQUEST,
            });
            const response = await api.postUpcomingCatalogRegistration({ authToken, catalogId, deployment });
            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: { catalogRegistration: response.data },
                type: LOAD_UPCOMING_CATALOG_REGISTRATION_SUCCESS,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { catalogId },
            payload: error,
            type: LOAD_UPCOMING_CATALOG_REGISTRATION_FAIL,
        });
    }
};

const shouldFetchUpcomingCatalogRegistration = (state, catalogId) => {
    const authToken = getAuthToken(state);
    if (!catalogId || !authToken) return false;
    const loading = isCatalogRegistrationLoading(state, catalogId);
    return !loading;
};

export const fetchUpcomingCatalogRegistrationIfNeeded = (catalogId: number) => async (
    dispatch: Function,
    getState: Function,
) => {
    if (shouldFetchUpcomingCatalogRegistration(getState(), catalogId)) {
        return dispatch(fetchUpcomingCatalogRegistration(catalogId));
    }
    return Promise.resolve();
};
